/**
 * AgentScheduleEditor: horário do agente por dia da semana e o que fazer com a
 * mensagem que chega fora do horário.
 *
 * Pedido da Gelo Americano (08 e 10/10/2026): "segunda a sexta das 8 às 18,
 * sábado das 8 ao meio-dia, domingo não" não cabia no editor antigo (um
 * período só para todos os dias); a cliente inverteu e deixou 18:01 às 07:59
 * sem dia marcado. E o cliente que escreve no domingo precisa de resposta na
 * segunda, sem furar a janela de 24 h dos canais Meta.
 *
 * Formato salvo (autopilot_schedule): { enabled, weekly, off_hours, slots }.
 * `weekly` é a fonte da tela; `slots` é derivado (um por horário) e continua
 * sendo o que o backend consulta para "está no horário?". O backend normaliza
 * de novo ao salvar (services/autopilot_off_hours.normalize_schedule).
 */
import React from 'react';
import { Info } from 'lucide-react';

// Ordem de exibição: semana começa na segunda; o índice é o do backend (0 = domingo).
const DAYS = [
  { value: 1, label: 'Segunda' },
  { value: 2, label: 'Terça' },
  { value: 3, label: 'Quarta' },
  { value: 4, label: 'Quinta' },
  { value: 5, label: 'Sexta' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
];

const DEFAULT_MESSAGE = 'Olá! Nosso atendimento funciona {{horario}}. Recebemos a sua mensagem e retornamos {{proxima_abertura}}.';

export function emptyWeekly() {
  const w = {};
  for (let d = 0; d < 7; d += 1) w[String(d)] = { enabled: false, start: '08:00', end: '18:00' };
  return w;
}

/** Doc antigo (só `slots`) visto pela tela nova. */
export function slotsToWeekly(slots) {
  const w = emptyWeekly();
  (slots || []).forEach((slot) => {
    (slot?.days || []).forEach((d) => {
      const k = String(d);
      if (w[k] && !w[k].enabled) {
        w[k] = { enabled: true, start: slot.start_time || '08:00', end: slot.end_time || '18:00' };
      }
    });
  });
  return w;
}

/** Um slot por horário; dias com o mesmo horário ficam juntos. */
export function weeklyToSlots(weekly) {
  const groups = new Map();
  for (let d = 0; d < 7; d += 1) {
    const e = weekly?.[String(d)];
    if (!e?.enabled) continue;
    const key = `${e.start || '08:00'}|${e.end || '18:00'}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(d);
  }
  return Array.from(groups.entries()).map(([key, days]) => {
    const [start_time, end_time] = key.split('|');
    return { days, start_time, end_time };
  });
}

export function withWeekly(schedule) {
  const weekly = schedule?.weekly && Object.keys(schedule.weekly).length
    ? { ...emptyWeekly(), ...schedule.weekly }
    : slotsToWeekly(schedule?.slots);
  return { ...schedule, weekly, slots: weeklyToSlots(weekly) };
}

const OFF_HOURS_MODES = [
  { id: 'ignore', label: 'Não responder', hint: 'A mensagem fica no inbox para a equipe.' },
  { id: 'reply', label: 'Avisar que está fora do horário', hint: 'Uma mensagem por período fechado, e só.' },
  { id: 'resume', label: 'Avisar e retomar na abertura', hint: 'O agente responde de verdade no próximo horário.' },
];

const AgentScheduleEditor = ({
  schedule,
  onChange,
  activeNow = false,
  colors,
  offHoursEnabled = false,
  businessTemplates = [],
  hasBusiness = false,
}) => {
  const sch = withWeekly(schedule || {});
  const weekly = sch.weekly;
  const offHours = {
    mode: 'ignore', message: '', template_mode: 'fixed', template_name: '', templates: [], agent_prompt: '',
    ...(sch.off_hours || {}),
  };

  const emit = (next) => onChange({ ...sch, ...next, slots: weeklyToSlots(next.weekly || weekly) });
  const setDay = (d, patch) => emit({ weekly: { ...weekly, [String(d)]: { ...weekly[String(d)], ...patch } } });
  const setOff = (patch) => emit({ off_hours: { ...offHours, ...patch } });
  const copyMondayToWeekdays = () => {
    const base = weekly['1'];
    const next = { ...weekly };
    [2, 3, 4, 5].forEach((d) => { next[String(d)] = { ...base }; });
    emit({ weekly: next });
  };

  const s = {
    box: {
      padding: '16px', backgroundColor: colors.bgSecondary, borderRadius: '10px',
      marginBottom: '4px', border: `1px solid ${colors.borderColor}`,
    },
    label: { fontSize: '12px', fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: '8px' },
    row: { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' },
    dayName: { width: '72px', fontSize: '13px', color: colors.textPrimary },
    time: {
      padding: '6px 8px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgPrimary, color: colors.textPrimary, fontSize: '13px', outline: 'none',
    },
    switchBase: (on) => ({
      width: '34px', height: '20px', borderRadius: '10px', position: 'relative', cursor: 'pointer', flexShrink: 0,
      backgroundColor: on ? colors.accentPrimary : colors.borderColor, transition: 'background-color 0.15s',
      border: 'none', padding: 0,
    }),
    knob: (on) => ({
      position: 'absolute', top: '2px', left: '2px', width: '16px', height: '16px', borderRadius: '50%',
      backgroundColor: '#fff', transform: on ? 'translateX(14px)' : 'translateX(0)', transition: 'transform 0.15s',
    }),
    linkBtn: {
      background: 'transparent', border: 'none', color: colors.accentPrimary, fontSize: '12px',
      fontWeight: 500, cursor: 'pointer', padding: 0,
    },
    modeCard: (on) => ({
      display: 'flex', flexDirection: 'column', gap: '2px', padding: '10px 12px', borderRadius: '8px',
      border: `1px solid ${on ? colors.accentPrimary : colors.borderColor}`,
      backgroundColor: on ? `${colors.accentPrimary}10` : 'transparent', cursor: 'pointer', textAlign: 'left',
    }),
    textarea: {
      width: '100%', minHeight: '64px', padding: '8px 10px', borderRadius: '8px', fontSize: '13px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary, color: colors.textPrimary,
      outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
    },
    select: {
      width: '100%', padding: '8px 10px', borderRadius: '8px', fontSize: '13px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    },
    hint: { fontSize: '11px', color: colors.textTertiary, lineHeight: 1.4 },
    info: {
      display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', marginTop: '12px',
      backgroundColor: `${colors.accentPrimary}08`, borderRadius: '8px', border: `1px solid ${colors.accentPrimary}15`,
      fontSize: '11px', color: colors.accentPrimary, lineHeight: 1.4,
    },
  };

  const showTemplates = offHoursEnabled && offHours.mode === 'resume' && hasBusiness;

  return (
    <div style={s.box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: activeNow ? '#10B981' : colors.textTertiary }} />
        <span style={{ fontSize: '12px', fontWeight: 500, color: activeNow ? '#10B981' : colors.textTertiary }}>
          {activeNow ? 'Ativo agora' : 'Fora do horário'}
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ ...s.label, marginBottom: 0 }}>Dias e horários</span>
        <button type="button" style={s.linkBtn} onClick={copyMondayToWeekdays} title="Copia o horário de segunda para terça a sexta">
          Repetir segunda até sexta
        </button>
      </div>
      <div style={{ marginTop: '6px' }}>
        {DAYS.map(({ value, label }) => {
          const e = weekly[String(value)];
          return (
            <div key={value} style={s.row}>
              <button
                type="button"
                role="switch"
                aria-checked={e.enabled}
                aria-label={`${label}: ${e.enabled ? 'atende' : 'fechado'}`}
                style={s.switchBase(e.enabled)}
                onClick={() => setDay(value, { enabled: !e.enabled })}
              >
                <span style={s.knob(e.enabled)} />
              </button>
              <span style={{ ...s.dayName, opacity: e.enabled ? 1 : 0.55 }}>{label}</span>
              {e.enabled ? (
                <>
                  <input type="time" value={e.start} onChange={(ev) => setDay(value, { start: ev.target.value })} style={s.time} aria-label={`${label}, início`} />
                  <span style={{ fontSize: '12px', color: colors.textTertiary }}>às</span>
                  <input type="time" value={e.end} onChange={(ev) => setDay(value, { end: ev.target.value })} style={s.time} aria-label={`${label}, fim`} />
                  {e.start > e.end && <span style={s.hint}>vira a noite</span>}
                </>
              ) : (
                <span style={{ fontSize: '12px', color: colors.textTertiary }}>Fechado</span>
              )}
            </div>
          );
        })}
      </div>

      {offHoursEnabled && (
        <div style={{ marginTop: '16px' }}>
          <span style={s.label}>Quando o cliente escreve fora do horário</span>
          <div style={{ display: 'grid', gap: '8px' }}>
            {OFF_HOURS_MODES.map((m) => (
              <button key={m.id} type="button" style={s.modeCard(offHours.mode === m.id)} onClick={() => setOff({ mode: m.id })}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>{m.label}</span>
                <span style={s.hint}>{m.hint}</span>
              </button>
            ))}
          </div>

          {offHours.mode !== 'ignore' && (
            <div style={{ marginTop: '12px' }}>
              <span style={s.label}>Mensagem de aviso</span>
              <textarea
                style={s.textarea}
                value={offHours.message}
                placeholder={DEFAULT_MESSAGE}
                onChange={(ev) => setOff({ message: ev.target.value })}
              />
              <span style={s.hint}>
                Pode usar {'{{proxima_abertura}}'} (ex.: "segunda-feira às 08:00") e {'{{horario}}'} (ex.: "de segunda a sexta, das 08:00 às 18:00"). Em branco, vale o texto de exemplo.
              </span>
            </div>
          )}

          {showTemplates && (
            <div style={{ marginTop: '12px' }}>
              <span style={s.label}>WhatsApp Business: se já passaram 24 h</span>
              <span style={{ ...s.hint, display: 'block', marginBottom: '8px' }}>
                A Meta só aceita template aprovado depois de 24 h sem resposta. Instagram e Messenger não têm template: a conversa fica registrada para a equipe.
              </span>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {[{ id: 'fixed', label: 'Template fixo' }, { id: 'agent', label: 'O agente escolhe' }].map((t) => (
                  <button key={t.id} type="button" style={{ ...s.modeCard(offHours.template_mode === t.id), flex: 1 }} onClick={() => setOff({ template_mode: t.id })}>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>{t.label}</span>
                  </button>
                ))}
              </div>
              {offHours.template_mode === 'fixed' ? (
                <select style={s.select} value={offHours.template_name} onChange={(ev) => setOff({ template_name: ev.target.value })}>
                  <option value="">Selecione um template aprovado</option>
                  {businessTemplates.map((t) => (
                    <option key={t.name} value={t.name}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <>
                  <div style={{ display: 'grid', gap: '6px', marginBottom: '8px' }}>
                    {businessTemplates.map((t) => {
                      const on = (offHours.templates || []).includes(t.name);
                      return (
                        <label key={t.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => setOff({
                              templates: on
                                ? (offHours.templates || []).filter((n) => n !== t.name)
                                : [...(offHours.templates || []), t.name],
                            })}
                          />
                          {t.name}
                        </label>
                      );
                    })}
                    {businessTemplates.length === 0 && (
                      <span style={s.hint}>Nenhum template aprovado sem variáveis. Aprove um na tela Templates da Meta.</span>
                    )}
                  </div>
                  <textarea
                    style={{ ...s.textarea, minHeight: '48px' }}
                    value={offHours.agent_prompt}
                    placeholder="Como escolher (opcional). Ex.: use o de bom dia se o cliente ainda não foi atendido."
                    onChange={(ev) => setOff({ agent_prompt: ev.target.value })}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div style={s.info}>
        <Info size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
        <span>
          O agente responde sozinho nos dias e horários marcados.
          {offHoursEnabled ? ' Fora deles, vale a regra acima.' : ' Fora deles, a mensagem fica para a equipe.'}
        </span>
      </div>
    </div>
  );
};

export default AgentScheduleEditor;
